import React, { useState, useEffect } from "react";
// Step components
import Step1 from "./OrderServices/Step1.jsx";
import Step2 from "./OrderServices/Step2.jsx";
import Step3 from "./OrderServices/Step3.jsx";
import Step4 from "./OrderServices/Step4.jsx";
import Step5 from "./OrderServices/Step5.jsx";

// NOTE: Later steps (Services, Property, Parties, Contact) will be added.

const steps = [
  { id: 1, label: "Transactions" },
  { id: 2, label: "Services" },
  { id: 3, label: "Property" },
  { id: 4, label: "Parties" },
  { id: 5, label: "Contact" },
];

const FormOrderServices = props => {
  const { locations = [] } = props;
  // Author-configured recipients from Sitecore Office Detail (Office Services To / Subject / Cc)
  const [officeEmailTo, setOfficeEmailTo] = useState("");
  const [officeEmailSubject, setOfficeEmailSubject] = useState("");
  const [officeEmailCc, setOfficeEmailCc] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    isInUS: "Inside the U.S.",
    // Step 1 fields
    propertyLocation: "inside",
    transactionType: "",
    loanType: "",
    salesPrice: "",
    loanAmount: "",
    stewartLocation: "",
    servicesSelected: [], // array of service keys
    specialInstructions: "",
    dateNeededBy: "", // ISO date string
    // Property step fields (Step 3)
    propertyStreetNum: "",
    propertyStreetName: "",
    propertyCity: "",
    propertyState: "",
    propertyZip: "",
    propertyCounty: "",
    propertyUnitNumber: "",
    propertyTaxParcelNumber: "",
    propertyDesc: "",
  });

  const [clientIp, setClientIp] = useState("");

  async function fetchIPAddress() {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      if (response.ok) {
        const result = await response.json();
        return result.ip || "";
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Failed to fetch IP address", err);
    }
    return "";
  }

  useEffect(() => {
    // Fetch IP once on mount.
    fetchIPAddress().then(ip => setClientIp(ip));
  }, []);

  const updateField = (name, value) => {
    if (name === "stewartLocation") {
      const location = locations.find(loc => loc.Value === value);
      if (location) {
        // Defaults when Sitecore author left fields empty — mail still sends
        setOfficeEmailTo(
          location.EmailFields?.To || "orders@stewart.com",
        );
        setOfficeEmailSubject(
          location.EmailFields?.Subject || "Order Services",
        );
        setOfficeEmailCc(location.EmailFields?.Cc || "");
      } else {
        setOfficeEmailTo("orders@stewart.com");
        setOfficeEmailSubject("Order Services");
        setOfficeEmailCc("");
      }
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const goNext = e => {
    if (e) e.preventDefault();
    // Basic required validation for step 1 only
    if (currentStep === 1) {
      if (!formData.transactionType || !formData.loanType) return;
    }
    setCurrentStep(s => Math.min(s + 1, steps.length));
  };

  const goPrev = e => {
    if (e) e.preventDefault();
    setCurrentStep(s => Math.max(1, s - 1));
  };

  /**
   * Build the JSON payload expected by the Order Services endpoint.
   * This maps internal form state (mostly camelCase) to the required
   * external field names (exact keys from sample output).
   *
   * NOTE: Some meta values (ip, token, etc.) are not collected yet; placeholders are used.
   * These can be supplied later via props or async lookups.
   */
  const buildOrderServicesPayload = (state, meta = {}) => {
    const {
      isInUS,
      transactionType, // set in Step1
      transaction_type, // legacy underscore variant (fallback)
      loanType,
      loan_type, // legacy fallback
      salesPrice,
      loanAmount,
      stewartLocation,
      services_selected = [], // legacy name
      servicesSelectedArr = [], // current preferred name
      specialInstructions,
      dateNeededBy,
      // Property
      propertyStreetNum,
      propertyStreetName,
      propertyCity,
      propertyState,
      propertyZip,
      propertyCounty,
      propertyUnitNumber,
      propertyTaxParcelNumber,
      propertyDesc,
      property_legal_description, // fallback
      // Parties (added in Step4)
      sellers = [],
      buyers = [],
      lenders = [],
      realEstateAgents = [],
      // Contact (Step5)
      role,
      firstName,
      lastName,
      email,
      phone,
      comments,
      sendCopy,
      agree,
    } = state;

    // Map selected services keys to labels (current mock list lives in Step2)
    // To avoid importing SERVICE list here, derive label from DOM-independent map.
    const SERVICE_LABELS = {
      closing: "Closing/Settlement Services",
      commitment: "Commitment/Prelim/Binder/Title Policy",
      foreclosure: "Foreclosure Search/TSG",
      ownerships: "Ownerships & Encumbrances",
      search: "Search/Title Report",
    };
    const combinedServiceKeys = [
      ...new Set([
        ...(services_selected || []),
        ...(servicesSelectedArr || []),
      ]),
    ];
    const servicesSelectedLabels = combinedServiceKeys
      .map(k => SERVICE_LABELS[k])
      .filter(Boolean);

    // If a single service matches sample expectation; else join with comma.
    const servicesSelectedStr = servicesSelectedLabels.join(", ");

    // Helper to normalize empty string vs undefined
    const safe = v => (v === undefined ? "" : v);

    // Normalize parties to match required keys. Fill missing keys with empty strings so backend receives consistent shape.
    const normalizePerson = p => ({
      existingContact: safe(p.existingContact), // UI does not yet support this field
      company: safe(p.companyName || p.company),
      firstName: safe(p.firstName),
      middleName: safe(p.middleName),
      lastName: safe(p.lastName),
      addressLine1: safe(p.addressLine1),
      addressLine2: safe(p.addressLine2),
      city: safe(p.city),
      state: safe(p.state),
      zip: safe(p.zip),
      email: safe(p.email),
      phone: safe(p.phone),
      // lender specific fields kept at top-level lender object, will include below
      loanNumber: safe(p.loanNumber),
      fax: safe(p.fax),
      licenseNumber: safe(p.licenseNumber),
      isSellingAgent: p.agentType
        ? p.agentType === "both"
          ? "Both"
          : p.agentType === "selling"
            ? "Selling"
            : "Listing"
        : safe(p.isSellingAgent),
    });

    const lenderNormalize = p => ({
      company: safe(p.companyName || p.company),
      loanNumber: safe(p.loanNumber),
      firstName: safe(p.firstName),
      lastName: safe(p.lastName),
      addressLine1: safe(p.addressLine1),
      city: safe(p.city),
      state: safe(p.state),
      zip: safe(p.zip),
      phone: safe(p.phone),
      fax: safe(p.fax),
      email: safe(p.email),
    });

    const agentNormalize = p => ({
      isSellingAgent: p.agentType
        ? p.agentType === "both"
          ? "Both"
          : p.agentType === "selling"
            ? "Selling"
            : "Listing"
        : safe(p.isSellingAgent),
      companyName: safe(p.companyName),
      firstName: safe(p.firstName),
      lastName: safe(p.lastName),
      licenseNumber: safe(p.licenseNumber),
      email: safe(p.email),
      phone: safe(p.phone),
      fax: safe(p.fax),
    });

    const data = {
      __id__: new Date().valueOf() + Math.random(),
      isInUS: safe(isInUS),
      transactionType: safe(transactionType || transaction_type),
      loanType: safe(loanType || loan_type),
      salesPrice: safe(salesPrice),
      loanAmount: safe(loanAmount),
      stewartLocation: safe(stewartLocation),
      servicesSelected: servicesSelectedStr, // string per sample
      specialInstructions: safe(specialInstructions),
      dateNeededBy: safe(dateNeededBy),
      propertyStreetNum: safe(propertyStreetNum),
      propertyStreetName: safe(propertyStreetName),
      propertyCity: safe(propertyCity),
      propertyState: safe(propertyState),
      propertyZip: safe(propertyZip),
      propertyCounty: safe(propertyCounty),
      propertyUnitNumber: safe(propertyUnitNumber),
      propertyTaxParcelNumber: safe(propertyTaxParcelNumber),
      propertyDesc: safe(propertyDesc || property_legal_description),
      sellers: sellers.map(normalizePerson),
      buyers: buyers.map(normalizePerson),
      lenders: lenders.map(lenderNormalize),
      agents: realEstateAgents.map(agentNormalize),
      existingContact: "", // top-level, UI pending
      role: safe(role),
      firstName: safe(firstName),
      lastName: safe(lastName),
      email: safe(email),
      phone: safe(phone),
      comments: safe(comments),
      sendCopy: safe(String(sendCopy || false)),
      agree: safe(String(agree || false)),
      clientIpAddress: meta.clientIpAddress || clientIp || "",
      userAgent:
        meta.userAgent ||
        (typeof navigator !== "undefined" ? navigator.userAgent : ""),
      assignedFormID: meta.assignedFormID || "", // placeholder
    };

    return {
      data,
      ...(meta.formUrl ? { formUrl: meta.formUrl } : {}),
      pageUrl:
        meta.pageUrl ||
        (typeof window !== "undefined" ? window.location.href : ""),
      token: meta.token || "", // placeholder security token
      // Author Office Services To / Subject / Cc — Lambda prefers these over the email sheet.
      // Empty Sitecore fields still send: To/Subject defaults; Lambda always adds orders@ CC.
      to: officeEmailTo || "orders@stewart.com",
      subject: officeEmailSubject || "Order Services",
      cc: officeEmailCc || "",
    };
  };

  const handleSubmit = async e => {
    e.preventDefault();
    // Ensure we have IP before building payload if not already fetched
    let ip = clientIp;
    if (!ip) {
      ip = await fetchIPAddress();
      setClientIp(ip);
    }
    const payload = buildOrderServicesPayload(formData, {
      ...(props.meta || {}),
      clientIpAddress: ip,
    });

    const { grecaptcha } = window;

    // Site Key owned by Stewart Title
    // As of August 2025, was not configured to work with localhost requests
    // so this will never submit successfully in Storybook.
    const SITE_KEY = `6Lc8rwYaAAAAAGsJKqZhD-FjPHtuq1D56kx47AnM`;

    const grecaptchaToken = await grecaptcha.execute(SITE_KEY, {
      action: "submit",
    });

    if (grecaptchaToken) {
      payload.token = grecaptchaToken;
      // eslint-disable-next-line no-console
      // console.log("Order Services Submit Payload", payload);
      try {
        const response = await fetch(
          "https://iwrsaiki86.execute-api.us-east-1.amazonaws.com/api/forms/submit",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          },
        );
        if (response.ok) {
          const json = await response.json();
          if (json?.submit?.status !== "success") {
            throw new Error(json?.submit?.message || "failed to submit form.");
          }

          if (
            document.querySelector(".form-order-services__message--success")
          ) {
            const successMsg = document.querySelector(
              ".form-order-services__message--success",
            );
            successMsg.classList.add("is-visible");
            successMsg.scrollIntoView({ behavior: "smooth" });
          }
        } else {
          const error = await response.text();
          const errMsg = document.querySelector(
            ".form-order-services__message--error",
          );
          errMsg.classList.add("is-visible");
          errMsg.scrollIntoView({ behavior: "smooth" });
          throw new Error(error);
        }
      } catch (error) {
        const errMsg = document.querySelector(
          ".form-order-services__message--error",
        );
        errMsg.classList.add("is-visible");
        errMsg.scrollIntoView({ behavior: "smooth" });
        submissionFailure(error, form);
      }
    }
  };

  return (
    <form className="form-order-services" onSubmit={handleSubmit}>
      <div className="form-order-services__message form-order-services__message--error">
        <div>
          <strong>An error has occurred!</strong> Please contact{" "}
          <a href="mailto:webmaster@stewart.com">webmaster@stewart.com</a> and
          let us know the name and URL of the form you just tried to complete.
          Something happened and it didn’t accept your information. We
          apologize!
        </div>
      </div>
      <div className="form-order-services__message form-order-services__message--success">
        <div>
          <strong>Success!</strong> Thank you for filling out our form. We have
          received it and will get back to you soon.
        </div>
      </div>
      <div className="form-order-services__wrapper">
        <nav
          className="form-order-services__steps-nav"
          aria-label="Order Services Steps"
        >
          <div className="form-order-services__steps-mobile-counter">
            Step {currentStep} of {steps.length}
          </div>
          <div className="form-order-services__steps-progress-bars">
            <div
              className={`form-order-services__steps-progress-bar ${currentStep === 1 ? "is-active" : ""}`}
            />
            <div
              className={`form-order-services__steps-progress-bar ${currentStep === 2 ? "is-active" : ""}`}
            />
            <div
              className={`form-order-services__steps-progress-bar ${currentStep === 3 ? "is-active" : ""}`}
            />
            <div
              className={`form-order-services__steps-progress-bar ${currentStep === 4 ? "is-active" : ""}`}
            />
            <div
              className={`form-order-services__steps-progress-bar ${currentStep === 5 ? "is-active" : ""}`}
            />
          </div>
          <ol>
            {steps.map(step => (
              <li
                key={step.id}
                className={`form-order-services__steps-nav-item ${
                  currentStep === step.id ? "is-active" : ""
                }`}
              >
                <div className="form-order-services__steps-nav-circle">
                  {step.id}
                </div>
                <span className="form-order-services__steps-nav-label">
                  {step.label}
                </span>
              </li>
            ))}
          </ol>
        </nav>
        <div className="form-order-services__content">
          {currentStep === 1 && (
            <Step1
              formData={formData}
              updateField={updateField}
              goNext={goNext}
              locations={locations}
            />
          )}
          {currentStep === 2 && (
            <Step2
              formData={formData}
              updateField={updateField}
              goNext={goNext}
              goPrev={goPrev}
            />
          )}
          {currentStep === 3 && (
            <Step3
              formData={formData}
              updateField={updateField}
              goNext={goNext}
              goPrev={goPrev}
            />
          )}
          {currentStep === 4 && (
            <Step4
              formData={formData}
              updateField={updateField}
              goNext={goNext}
              goPrev={goPrev}
            />
          )}
          {currentStep === 5 && (
            <Step5
              formData={formData}
              updateField={updateField}
              goPrev={goPrev}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </div>
    </form>
  );
};

export default FormOrderServices;
